import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ModalOverlay,
  PopoverCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Modal,
  Text,
  Button,
  HStack,
  PopoverArrow,
  PlacementWithLogical,
  useMediaQuery,
  Image,
} from '@chakra-ui/react';
import {
  useAppDispatch,
  useSetStepMutation,
  setStep,
} from '@spartalabs/pdc-core';
import {
  AddsNameAndImage,
  AddsWidgetAndColor,
  ChangeSizeAndOrder,
} from 'assets';
import { useCustomToast } from 'hooks';
import useAnalyticsEvent from 'hooks/useAnalyticsEvent';
import { GA_ONBOARDING } from 'utils/constants';

interface SideBarProps {
  children: ReactNode;
  step: number;
  isOpen: boolean;
  placement: PlacementWithLogical;
}

export default function Onboarding({
  children,
  step,
  isOpen,
  placement,
}: SideBarProps) {
  const [show, setShow] = useState(false);

  const [{ loadingFoward, loadingBack }, setLoading] = useState({
    loadingFoward: false,
    loadingBack: false,
  });

  const [isLargerThan980] = useMediaQuery('(min-width: 980px)');

  const dispatch = useAppDispatch();
  const { handleToast } = useCustomToast();

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_ONBOARDING,
  });

  const [useStep, { isLoading }] = useSetStepMutation();

  const { title, subtitle, gif } = useMemo(() => {
    switch (step) {
      case 2:
        return {
          title: 'Personalização geral',
          subtitle: (
            <Text fontSize="xs" color="text.400">
              Personalize a página inicial do portal com a cor que mais gera
              identificação com sua marca e organize os principais índices de
              maneira simples e prática.
              <br />
              <br />
              Aqui você também pode organizar a posição dos quadros e gráficos,
              arrastando e soltando os quadros em toda tela do dashboard.
            </Text>
          ),
          gif: AddsWidgetAndColor,
        };
      case 3:
        return {
          title: 'Configurações dos quadros',
          subtitle: (
            <Text fontSize="xs" color="text.400">
              Ajuste o tamanho da exibição dos quadros ou remova o quadro do
              dashboard.
            </Text>
          ),
          gif: ChangeSizeAndOrder,
        };
      case 4:
        return {
          title: 'Marca e título do dashboard',
          subtitle: (
            <Text fontSize="xs" color="text.400">
              Insira sua marca no topo do nosso dashboard, para gerar mais
              conexão com seu negócio.
            </Text>
          ),
          gif: AddsNameAndImage,
        };

      default:
        return { title: 'Ocorreu um erro', subtitle: 'Ocorreu um erro' };
    }
  }, [step]);

  const handleStep = useCallback(
    async (direction: number) => {
      if (isLoading) return;

      try {
        if (direction > step)
          setLoading({ loadingFoward: true, loadingBack: false });
        else setLoading({ loadingFoward: false, loadingBack: true });

        await useStep(direction).unwrap();

        //Onboarding Analytics
        if (step !== 4 && direction === 5) {
          analyticsEvent(`Fechado no modal ${title}`);
        }
        if (step === 4 && direction === 5) {
          analyticsEvent('Onboarding concluído');
        }

        dispatch(setStep({ step: direction }));
      } catch {
        handleToast({
          step: 4,
          action: 'realizar o onboarding',
        });
      } finally {
        setLoading({ loadingFoward: false, loadingBack: false });
      }
    },
    [isLoading],
  );

  useEffect(() => {
    if (isOpen) setTimeout(() => setShow(true), 300);
    else setShow(false);
  }, [isOpen]);

  return (
    <Popover
      returnFocusOnClose={false}
      isOpen={isOpen}
      placement={isLargerThan980 ? placement : 'bottom'}
      closeOnBlur={false}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Modal isOpen={show} onClose={() => null}>
        <ModalOverlay>
          <PopoverContent w={{ base: '370px', md: '548px' }} p="24px">
            <PopoverArrow />
            <PopoverCloseButton
              onClick={() => handleStep(5)}
              size="16px"
              color="black"
            />
            <Text fontWeight={600} color="text.400">
              {title}
            </Text>

            {subtitle}

            <Image mt="40px" h="260px" w="100%" src={gif} />

            <HStack ml="auto" mt="40px">
              <Button
                isDisabled={loadingFoward}
                isLoading={loadingBack}
                onClick={() => handleStep(step - 1)}
                variant="outline"
              >
                Voltar
              </Button>
              <Button
                isDisabled={loadingBack}
                isLoading={loadingFoward}
                onClick={() => handleStep(step + 1)}
              >
                Continuar
              </Button>
            </HStack>
          </PopoverContent>
        </ModalOverlay>
      </Modal>
    </Popover>
  );
}
